import { Injectable } from '@angular/core';

import { GameType } from '@bpos/v1/sports-promo';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { hasValue } from '@frontend/sports/common/core/utils/extended-types';
import { flatten, keyBy, uniq } from 'lodash-es';

import { Promotion } from '../crm-offer-data/crm-offer.model';

@Injectable({ providedIn: 'root' })
export class CrmPromotionMapperService {
    getBySport(promotions: Promotion[], sportId: number): Promotion[] {
        return promotions.filter((p) => p.banner && p.allFilters.some((f) => f.sportIds.some((spId) => spId === sportId)));
    }

    getByCompetition(promotions: Promotion[], competitionIds: number[]): Promotion[] {
        const competitions = keyBy(competitionIds);

        return promotions.filter((p) => p.banner && p.allFilters.some((f) => f.competitionIds.some((id) => !!competitions[id])));
    }

    getPromotionsForEvent(event: EventModel, promotions: Promotion[]): Promotion[] {
        return promotions.filter((p) => this.isGameType(event.live, p));
    }

    getPromotedOptionGroupIds(
        optionGroups: EventOptionGroup[],
        event: EventModel,
        filteredPromotions: Promotion[],
    ): { [optionGroupId: string]: string } {
        if (!filteredPromotions.length) {
            return {};
        }

        const allFilters = flatten(filteredPromotions.map((fp) => fp.allFilters));
        const allMarketTemplateIds = flatten(allFilters.map((af) => af.marketTemplateIds));
        const allMarketIds = flatten(allFilters.map((af) => af.marketIds));

        let promotedGroups = optionGroups.filter(
            (group) => !group.groupedMarkets && allMarketTemplateIds.some((templateId) => templateId === group.templateId),
        );
        const promotedGroupsByMarket = flatten(allMarketIds.map((marketId) => event.marketsDictionary[marketId]));

        promotedGroups = promotedGroups.concat(promotedGroupsByMarket).filter(hasValue);
        const promotedGroupIds = uniq(promotedGroups.map((g) => g.id));

        return keyBy(promotedGroupIds);
    }

    getBannerPromotions(optionGroups: EventOptionGroup[], event: EventModel, filteredPromotions: Promotion[]): Promotion[] {
        if (!filteredPromotions.length) {
            return [];
        }

        const eventTemplateGroupsDictionary = keyBy(optionGroups, (g) => g.templateId);
        const optionsDictionary = keyBy(flatten(optionGroups.map((og) => og.options)), (g) => g.id);

        return filteredPromotions.filter((filteredPromo) => {
            if (!filteredPromo.banner) {
                return false;
            }

            const hasPromo = filteredPromo.allFilters.some((f) => {
                const fixturePromo = f.fixtureIds.some((fId) => fId === event.id);
                if (fixturePromo) {
                    return true;
                }

                const templatePromo = f.marketTemplateIds.some((tId) => !!eventTemplateGroupsDictionary[tId]);
                if (templatePromo) {
                    return true;
                }

                const marketPromo = f.marketIds.some((marketId) => !!event.marketsDictionary[marketId]);
                if (marketPromo) {
                    return true;
                }

                const optionPromo = f.optionIds.some((optionId) => !!optionsDictionary[optionId]);

                return optionPromo;
            });

            return hasPromo;
        });
    }

    private isGameType(isEventLive: boolean, promotion: Promotion): boolean {
        return (
            promotion.gameType === GameType.Mixed ||
            (isEventLive && promotion.gameType === GameType.InPlay) ||
            (!isEventLive && promotion.gameType === GameType.PreMatch)
        );
    }
}
